import dayjs from 'dayjs';
import { get } from 'lodash';
import type { TMinimumTimeDistanceValidatorParams } from '@ballerine/common';

import type { TValidator } from '../../types';
import { formatDocumentId } from '../../../DynamicForm/utils/format-document-id';

const validateTimeDistance = (
  value: string,
  timeValue: number,
  timeUnit: 'days' | 'months' | 'years',
  message = 'The date is not within the minimum time distance.',
) => {
  const now = dayjs();
  const targetDate = dayjs(value).startOf('day');

  const minimumDate = now.subtract(timeValue, timeUnit).startOf('day');

  if (!targetDate.isSameOrBefore(minimumDate)) {
    throw new Error(message);
  }

  return true;
};

export const minimumTimeDistanceValueValidator: TValidator<
  string,
  TMinimumTimeDistanceValidatorParams
> = (value, params, schema, context) => {
  const { timeValue, timeUnit, valueDestination } = params.value;

  if (!timeValue || !timeUnit) {
    throw new Error('Time value and time unit are required.');
  }

  if (!['days', 'months', 'years'].includes(timeUnit)) {
    throw new Error('Time unit must be one of: days, months, years.');
  }

  const documentId = formatDocumentId({
    type: schema.metadata?.element?.params?.template?.type,
    category: schema.metadata?.element?.params?.template?.category,
    entityType: 'business',
    entityId: value,
  });

  const documentFile = context?._files[documentId];

  if (documentFile) {
    return true;
  }

  const dateValue: string = get(context, valueDestination ?? '');

  if (!dateValue || !dayjs(dateValue).isValid()) {
    return false;
  }

  return validateTimeDistance(dateValue, timeValue, timeUnit, params.message);
};
